import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Fade, Slide } from "@material-ui/core";
import { withStyles } from "@material-ui/styles";

import { useStyles } from "./OTPModalStyles";
import { useDispatch } from "react-redux";
import { CTAButton } from "../../../Components";
import { TransitionProps } from "@material-ui/core/transitions";
import { useMatchMediaQuery } from "../../../Hooks/useMatchMediaQuery";
import "./styles.scss";
import { CustomTextField } from "../../../Components/DeliveryChargeModal/style";
import {
  authenticateUser,
  captureLoginMerchantLeads,
  generateOTP,
  verifyOpenOTP,
  verifyOTP,
} from "../../../Redux/Actions";
import "./OTPModal.scss";
import { CancelVerifyModalIcon, PencilIcon } from "../utils/svgIcons";
import { getDomainCart, goToDomainCart } from "../utils/common";
import {
  clevertapEventPush,
  getEventPlatform,
  getUrlParamValue,
  isDomainCartPage,
  isDomainSearchPage,
} from "../../../Utils/common";
import { sendGAEvent } from "../../../Utils/GAEvents";
import { TRACKING_EVENTS } from "../../../Utils/GAEvents/gaEventsConstants";
import { DLP_WEBSITE } from "../../../Utils/cleverTapEventsConstant";
import { EVENT_TYPE } from "../../../Utils/constants";
import {
  initiateOTPSend,
  initOTPless,
  OTP_RESPONSE_TYPE,
  removeOTPLessSDK,
  RESPONSE_STATUS,
  verifyOTPLess,
} from "@/Hooks/useOTPLogin";
import { ToastNotifyError } from "@/Components/Toast";
import { useOTPLessForDLPSignIn } from "@/Constants/index";
import { isDummyPhoneNumber } from "@/Utils/authenticate";

// isUserCallbackExecuted is used as "ONETAP" event is fired multiple times in low network conditions (replicable on cart page)
let isUserCallbackExecuted = false;

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  const [isDesktop] = useMatchMediaQuery(["(min-width: 720px)"]);

  if (isDesktop) {
    return <Fade in={true} ref={ref} {...props} />;
  }
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomDialog = withStyles({
  root: {
    "& .MuiDialog-paperWidthSm": {
      maxWidth: 525,
      width: "100%",
      padding: "2rem 1.5rem",
      borderRadius: "12px",

      "@media screen and (max-width: 719px)": {
        maxWidth: "100%",
        position: "absolute",
        bottom: "-32px",
        padding: "20px",
      },
    },
    "& .MuiPaper-rounded": {
      borderRadius: 12,
      overflow: "hidden",
    },

    "& .MuiDialog-paperScrollPaper": {
      maxHeight: "100%",
    },
  },
})(Dialog);

export function OTPModal({ isVisible, closeModal, verifyModalFromHeader, postLoginHandler, isSignInAndPayFlow }) {
  // const classes = useStyles();
  const dispatch = useDispatch();
  // const [isDesktop] = useMatchMediaQuery(["(min-width: 720px)"]);
  const timerId = useRef(null);

  const [isOtpSent, setIsOtpSent] = useState(false);
  const [OTP, setOTP] = useState("");
  const [timeLeft, setTimeLeft] = useState(30);
  const [isVerificationLoading, setIsVerificationLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const domainCart = getDomainCart();

  const useOtpLess = useMemo(() => useOTPLessForDLPSignIn && !isDummyPhoneNumber(phoneNumber), [phoneNumber]);

  useEffect(() => {
    if (useOtpLess) {
      initOTPless(
        (...args) => {
          // eslint-disable-next-line
          onOTPVerificationComplete.apply(null, args);
        },
        { fromDLP: true },
      );
      return () => {
        removeOTPLessSDK();
      };
    }
  }, [useOtpLess]);

  useEffect(() => {
    return () => {
      clearInterval(timerId.current);
    };
  }, [isOtpSent]);

  function onOTPVerificationComplete(userData) {
    if (userData?.responseType === OTP_RESPONSE_TYPE.ONETAP && !isUserCallbackExecuted) {
      isUserCallbackExecuted = true;
      dispatch(
        verifyOTP({
          payload: {
            token: userData?.response?.token,
            source: "domain_landing_page",
          },
          callback: postVerifyOTPCallback,
        }),
      );
    }
  }

  const getOtp = (phoneNumber) => {
    const postGenerateOTPCallback = () => {
      setIsOtpSent(true);
      setCountdown();
      sendGAEventDetails(
        isDomainSearchPage()
          ? TRACKING_EVENTS.otpSearchResultPage
          : isDomainCartPage()
            ? TRACKING_EVENTS.otpCartPage
            : null,
      );
    };

    if (!useOtpLess) {
      dispatch(
        generateOTP({
          data: phoneNumber,
          callback: postGenerateOTPCallback,
          source: "DomainLandingPage",
        }),
      );
    } else {
      initiateOTPSend({ phone: phoneNumber }).then((data) => {
        dispatch(captureLoginMerchantLeads({ phone: phoneNumber, os: "DomainLandingPage" }));
        if (data.responseType !== OTP_RESPONSE_TYPE.INITIATE) {
          return;
        }
        if (data.statusCode !== RESPONSE_STATUS.SUCCESS.CODE) {
          ToastNotifyError(data?.errorMessage || data?.response?.errorMessage || "Please try again!");
          return;
        }
        postGenerateOTPCallback();
      });
    }
  };

  const changeNumber = () => {
    setOTP("");
    setIsOtpSent(false);
  };

  const onResendOtpClick = () => {
    getOtp(phoneNumber);
  };

  const sendGAEventDetails = (event, store_id = "") => {
    const { device } = getEventPlatform();
    sendGAEvent(event, {
      dimension1: getUrlParamValue("domain"),
      dimension2: device,
      dimension4: domainCart?.amount, // amount
      dimension5: phoneNumber, // number
      dimension6: "", //  position
      dimension7: store_id, //  sttore id
    });
  };

  const verifiedEventName = (eventType: string) => {
    if (eventType === EVENT_TYPE.GA_EVENT) {
      return isSignInAndPayFlow
        ? TRACKING_EVENTS.otpVerifiedSignIn
        : verifyModalFromHeader
          ? TRACKING_EVENTS.otpVerifiedHeader
          : TRACKING_EVENTS.otpVerified;
    }
    return isSignInAndPayFlow
      ? DLP_WEBSITE.OTP_VERIFIED_SIGNIN
      : verifyModalFromHeader
        ? DLP_WEBSITE.OTP_VERIFIED_HEADER
        : DLP_WEBSITE.OTP_VERIFIED;
  };

  function postVerifyOTPCallback(status: boolean, data: any) {
    if (status) {
      setIsOtpSent(false);
      setPhoneNumber("");
      setOTP("");
      closeModal(false);
      clevertapEventPush(verifiedEventName(EVENT_TYPE.CLEVER_TAP) as string, { identity: phoneNumber });
      sendGAEventDetails(verifiedEventName(EVENT_TYPE.GA_EVENT), data?.store?.owner?.store_id);
      dispatch(authenticateUser(data?.auth_token));
      postLoginHandler && postLoginHandler();
    }
    setTimeout(() => setIsVerificationLoading(false), 500);
  }

  const verifyTheOTP = () => {
    setIsVerificationLoading(true);
    if (!useOtpLess) {
      dispatch(
        verifyOpenOTP({
          phone: phoneNumber,
          otp: parseInt(OTP),
          source: window?.location?.href || "",
          callback: postVerifyOTPCallback,
        }),
      );
    } else {
      verifyOTPLess({
        phone: phoneNumber,
        otp: OTP,
      }).then((response) => {
        if (response?.statusCode !== RESPONSE_STATUS.SUCCESS.CODE) {
          ToastNotifyError(response?.response?.errorMessage || "We ran into some issue. Please try again.");
          setTimeout(() => setIsVerificationLoading(false), 500);
        }
      });
    }
  };

  const setCountdown = () => {
    setTimeLeft(30);
    clearInterval(timerId.current);
    timerId.current = setInterval(() => {
      setTimeLeft((timeLeft) => {
        if (timeLeft === 0) {
          return 0;
        }
        return --timeLeft;
      });
    }, 1000);
  };

  return (
    <CustomDialog
      open={isVisible}
      onClose={(event, reason) => {
        if (reason && reason == "backdropClick") {
          return;
        }
        closeModal(false);
      }}
      TransitionComponent={Transition}
      className="open-otp-modal-wrapper relative"
      disableBackdropClick
      disableEscapeKeyDown
    >
      <span
        className="verify_modal_cancel"
        onClick={() => {
          setIsOtpSent(false);
          setPhoneNumber("");
          closeModal(false);
        }}
      >
        <CancelVerifyModalIcon size="24px" color="#000" fontSize="medium" />
      </span>

      {!isOtpSent ? 
        <PhoneNumberSection
          getOtp={getOtp}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          verifyModalFromHeader={verifyModalFromHeader}
          closeModal={closeModal}
          sendGAEventDetails={sendGAEventDetails}
        />
       : 
        <OTPSection
          OTP={OTP}
          setOTP={setOTP}
          onResendOtpClick={onResendOtpClick}
          timeLeft={timeLeft}
          verifyTheOTP={verifyTheOTP}
          phoneNumber={phoneNumber}
          changeNumber={changeNumber}
          isVerificationLoading={isVerificationLoading}
        />
      }
    </CustomDialog>
  );
}

function PhoneNumberSection({
  getOtp,
  phoneNumber,
  setPhoneNumber,
  verifyModalFromHeader,
  closeModal,
  sendGAEventDetails,
}) {
  const classes: any = useStyles();
  const domainCart = getDomainCart();
  const sendSignInLaterGAEvent = () => {
    const { device } = getEventPlatform();
    sendGAEvent(TRACKING_EVENTS.domainCartSignInLaterClicked, {
      dimension1: getUrlParamValue("domain"),
      dimension2: device,
      dimension4: domainCart?.amount, // amount
      dimension5: phoneNumber, // number
      dimension6: "", //  position
      // Add more dimensions as needed
    });
  };
  return (
    <div className="phone_verify_wrapper">
      <div className="phone_verify_top_container">
        <p className="secondary_heading">Sign in or create a account</p>
        <p className="primary_heading">Enter your Mobile Number</p>
      </div>
      <div className="phone_verify_bottom_container">
        <div className="phone_verify_bottom_top_container">
          <div className="input_text_section">
            <p>Mobile number</p>
            <CustomTextField
              label=""
              id="outlined-margin-normal"
              className={classes.textFieldRoot}
              InputLabelProps={{
                shrink: true,
              }}
              color="secondary"
              autoComplete="off"
              variant="outlined"
              value={phoneNumber}
              placeholder="Enter Number"
              onChange={(e: any) => {
                if (e.target.value.length < 11) {
                  setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""));
                }
              }}
              onKeyPress={(e) => {
                if (e.which === 13) {
                  getOtp(phoneNumber);
                }
              }}
            />
          </div>
          <p>Yes, I want to receive important information & updates on my WhatsApp</p>
        </div>
        <div className="phone_verify_cta_container">
          <CTAButton
            variant="solid"
            onClick={() => getOtp(phoneNumber)}
            disabled={phoneNumber.trim().length < 10}
            customClasses="flex items-center"
            customStyles={{
              backgroundColor: phoneNumber.trim().length < 10 ? "#999999" : "#2F80ED",
              padding: "1rem 1.5rem",
              borderRadius: 8,
              width: "100%",
              border: "none",
              boxShadow: "none",
            }}
          >
            <div className="w-100 flex items-center justify-center">
              <p className="md-text16 fw4 ma0">Get OTP</p>
            </div>
          </CTAButton>
        </div>
        {!verifyModalFromHeader && 
          <div className="phone_verify_or_container">
            <p>OR</p>
          </div>
        }
        {!verifyModalFromHeader && 
          <div className="phone_verify_later_cta_container">
            <button
              onClick={() => {
                closeModal(false);
                sendGAEventDetails && sendGAEventDetails(TRACKING_EVENTS.domainCartSignInLaterClicked);
                goToDomainCart();
              }}
            >
              Skip for now
            </button>
          </div>
        }
      </div>
    </div>
  );
}

function OTPSection({
  OTP,
  setOTP,
  onResendOtpClick,
  timeLeft,
  verifyTheOTP,
  phoneNumber,
  changeNumber,
  isVerificationLoading,
}) {
  const classes: any = useStyles();

  return (
    <div className="phone_verify_wrapper">
      <div className="phone_verify_otp_wrapper">
        <p className="phone_verify_otp_sent_text">OTP sent to</p>
        <p className="phone_verify_sent_number_text">
          {" "}
          {phoneNumber}{" "}
          <span onClick={changeNumber}>
            {" "}
            <PencilIcon /> Edit number
          </span>
        </p>
      </div>
      <div className="phone_verify_input_box_wrapper">
        <div className="input_text_section">
          <p>Enter OTP</p>
          <CustomTextField
            label=""
            id="outlined-margin-normal"
            className={classes.textFieldRoot}
            margin="normal"
            variant="outlined"
            fullWidth
            autoComplete="off"
            InputLabelProps={{
              shrink: true,
            }}
            value={OTP}
            color={"primary"}
            placeholder="Enter 4 digit OTP"
            style={{ margin: 0 }}
            onChange={(e: any) => {
              if (e.target.value.length < 5) {
                setOTP(e.target.value.replace(/[^0-9]/g, ""));
              }
            }}
            onKeyPress={(e) => {
              if (e.which === 13) {
                verifyTheOTP(OTP);
              }
            }}
          />
          <div className="flex justify-center flex-column items-end ">
            <div className={`flex justify-center flex-column items-end mr1 ${!timeLeft ? "cp" : "cursor-not-allowed"}`}>
              <p onClick={() => !timeLeft && onResendOtpClick()} className={timeLeft > 0 ? null : "activate_resend"}>
                Resend {timeLeft > 0 ? timeLeft < 10 ? `in 00:0${timeLeft}` : `00:${timeLeft}` : `OTP`}
              </p>
            </div>
          </div>
        </div>
      </div>
      <CTAButton
        variant="solid"
        onClick={() => verifyTheOTP(OTP)}
        disabled={OTP.trim().length < 4 || isVerificationLoading}
        customClasses="flex items-center"
        customStyles={{
          backgroundColor: OTP.trim().length < 4 || isVerificationLoading ? "#999999" : "#2F80ED",
          padding: "1rem 1.5rem",
          borderRadius: 4,
          width: "100%",
          outline: "none",
          border: "none",
          boxShadow: "none",
          fontFamily: "Plus Jakarta Sans",
        }}
      >
        <div className="w-100 flex items-center justify-center">
          <p className="md-text16 fw4 ma0">{!isVerificationLoading ? "Verify OTP" : "Verifying...."}</p>
        </div>
      </CTAButton>
    </div>
  );
}
